// 17章 カーテンウォール工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_CW_CONCEPT = `
<svg viewBox="0 0 660 250" role="img" aria-label="カーテンウォールの概念。建物の骨組みの外側に、カーテンのように吊り下げる非耐力の外壁。建物の重さは支えず、自重と風圧のみを負担する。メタル系とPC系がある">
  <g stroke="currentColor" stroke-width="3">
    <line x1="120" y1="40" x2="120" y2="220"/>
    <line x1="240" y1="40" x2="240" y2="220"/>
    <line x1="120" y1="90" x2="240" y2="90"/>
    <line x1="120" y1="150" x2="240" y2="150"/>
  </g>
  <g fill="currentColor" opacity="0.4">
    <rect x="252" y="45" width="80" height="45"/>
    <rect x="252" y="95" width="80" height="45"/>
    <rect x="252" y="145" width="80" height="45"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="130" y="30">建物の骨組み（柱・梁）</text>
    <text x="345" y="70">カーテンウォール</text>
    <text x="345" y="120">（非耐力の外壁パネル）</text>
    <text x="345" y="170" opacity="0.85">骨組みの外側に吊り下げる</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="120" y="245">建物の重さは支えない。自重と風圧のみ負担 → 軽い外壁で高層建築を実現（17.1.1）</text>
  </g>
</svg>`;

const SVG_CW_TYPES = `
<svg viewBox="0 0 660 220" role="img" aria-label="カーテンウォールの2種類。メタルカーテンウォールはアルミ等の金属とガラスで構成され軽く意匠性が高い。PCカーテンウォールはプレキャストコンクリート製で重厚・耐火性が高くタイル等を先付けできる">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30" y="50" width="280" height="140" rx="10"/>
    <rect x="350" y="50" width="280" height="140" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="170" y="78">メタルカーテンウォール（2節）</text>
    <text x="490" y="78">PCカーテンウォール（3節）</text>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="46" y="108">・アルミ等の金属＋ガラス</text>
    <text x="46" y="132">・軽い・意匠性が高い</text>
    <text x="46" y="156">・高層オフィスの主流</text>
    <text x="366" y="108">・プレキャストコンクリート製</text>
    <text x="366" y="132">・重厚・耐火性が高い</text>
    <text x="366" y="156">・タイル等を先付けできる</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="210">共通の要求性能：耐風圧性・耐震性・水密性・気密性・耐火性・耐温度差性・遮音性・断熱性（17.1.2）</text>
  </g>
</svg>`;

const SVG_FASTENER = `
<svg viewBox="0 0 660 240" role="img" aria-label="ファスナー（取付け金物）の役割。躯体付け金物にファスナーでカーテンウォールを取り付け、地震で建物が変形してもパネルが追従して破損しないようにする。仮留め後に位置調整し本留めする">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="40" width="50" height="180"/></g>
  <g fill="currentColor" opacity="0.5"><rect x="400" y="40" width="30" height="180"/></g>
  <g fill="none" stroke="currentColor" stroke-width="3">
    <path d="M110 90 h80 v-15 h60"/>
    <path d="M110 160 h120 v15 h20"/>
  </g>
  <g fill="currentColor">
    <circle cx="255" cy="75" r="5"/>
    <circle cx="255" cy="175" r="5"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="50" y="30" opacity="0.85">躯体（柱・梁）</text>
    <text x="130" y="70">躯体付け金物</text>
    <text x="270" y="80">ファスナー</text>
    <text x="270" y="180">（地震変位に追従）</text>
    <text x="410" y="30" opacity="0.85">CW部材</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="235">躯体付け金物の許容差：鉛直±10mm・水平±25mm。仮留め→位置調整→本留めの順（17.2.5）</text>
  </g>
</svg>`;

const ch17: Record<string, Commentary> = {
  "17.1.1": {
    plainSummary:
      "この章は工場生産されたメタルカーテンウォール・PCカーテンウォールを用いる非耐力外壁工事に適用する。1章とセットで適用する。設計図書以外は承諾を受けて製造所仕様にできる。",
    why: "カーテンウォール（CW）は、建物の骨組みの外側に「カーテンのように」吊り下げる外壁です。建物の重さは支えず（非耐力）、自重と風圧だけを負担するので軽く、高層ビルを実現できます。工場で作って現場で取り付けるのが特徴です。",
    glossary: [
      {
        term: "カーテンウォール（CW）",
        meaning:
          "建物の荷重を負担しない、外周に吊り下げる壁。金属・ガラス製（メタルCW）とコンクリート製（PC CW）がある。",
      },
      {
        term: "非耐力壁",
        meaning: "建物の構造荷重（重さ）を支えない壁。カーテンウォールが代表。",
      },
    ],
    diagrams: [
      {
        svg: SVG_CW_CONCEPT,
        caption: "カーテンウォールの概念（17.1.1）",
      },
    ],
  },

  "17.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②所定の形状・寸法で見え掛り部が所要の仕上り ③耐風圧性・耐震性・水密性・気密性・耐火性・耐温度差性・遮音性・断熱性等の所定の性能を持ち、取合い部の処理が適切であること。",
    why: "CWは建物の外皮そのものなので、要求される性能が非常に多い。風・地震・雨・音・熱・火のすべてに対応します。特に「取合い部（パネルどうし・他部材との継ぎ目）」の処理が漏水・脱落を防ぐ鍵です。",
    diagrams: [
      {
        svg: SVG_CW_TYPES,
        caption: "メタルCWとPC CWの違い（17章の構成）",
      },
    ],
  },

  "17.1.3": {
    plainSummary:
      "諸性能値は特記。ファスナー部はCWの諸性能が十分確保される機構であること。性能の確認・判定方法は特記がなければ適切な資料で承諾を受ける。",
    why: "CWの性能は「ファスナー（取付け金物）」で決まります。地震で建物が変形してもパネルが追従して割れない、風圧で外れない、という機構がファスナーに求められます。",
    glossary: [
      {
        term: "ファスナー",
        meaning:
          "カーテンウォールを躯体に取り付ける金物。地震時の層間変形を吸収する可動機構を持つ。",
      },
    ],
  },

  "17.2.1": {
    plainSummary:
      "この節は主要構成部材に金属系材料を用いたメタルカーテンウォール工事に適用する。",
  },

  "17.2.2": {
    plainSummary:
      "金属系材料の種類は特記。シーリング材は9.7.2、ガラスは16.14.2による。ガラス取付けはシーリング材・構造ガスケット（JIS A 5760）。断熱材・摩擦低減材・取付け用金物は規定による（屋外のボルト・ナットはステンレス製）。",
    glossary: [
      {
        term: "構造ガスケット",
        meaning:
          "ガラスをはめ込んで固定する弾力性のあるゴム部材。ガラスの保持と止水を兼ねる。",
      },
    ],
  },

  "17.2.3": {
    plainSummary:
      "製品の寸法許容差は特記がなければ表17.2.1（形材の長さ±1.0〜2.0mm、パネル材の平面度2/1,000等）。取付け用金物の表面処理は表14.2.2（屋外はA種、屋内はE種）。ガラス溝寸法は特記または製造所仕様。",
    why: "CWは工場で高精度に作るため、寸法許容差が厳しく管理されます。ミリ単位の精度が、現場での取付け精度と目地の通りを決めます。",
  },

  "17.2.4": {
    plainSummary:
      "製作は17.1.3の性能を確保する。異種金属の接触腐食のおそれのある箇所は接触腐食防止対策。溶接で仕上げ面が変色・ゆがみを起こさないようにし、溶接部には防錆処理。",
    why: "アルミと鋼など異種金属を組み合わせるCWでは、電食（接触腐食）対策が重要。溶接の熱で表面が変色・変形しないよう、施工管理が求められます。",
  },

  "17.2.5": {
    plainSummary:
      "躯体付け金物はコンクリート打込みまたは鉄骨溶接であらかじめ取り付ける（取付け位置の許容差は鉛直±10mm・水平±25mm）。主要部材は所定の順序・方法で取り付け（許容差は目地幅±3mm等）、仮留め→位置調整→本留め。現場溶接の本留めはAz種錆止め塗料。耐火構造は技術的基準に適合。",
    why: "CW取付けの流れは「躯体付け金物→ファスナー→CW部材」の3段階。躯体付け金物は許容差が大きめ（水平±25mm）ですが、ファスナーの調整機構でこの誤差を吸収し、CW部材は目地幅±3mmの高精度で仕上げます。「粗く付けて精密に調整する」のがCW取付けの考え方です。",
    points: [
      "躯体付け金物→ファスナー→CW部材の順で取付け",
      "躯体金物の許容差は大きめ（水平±25mm）、CW部材は±3mm",
      "仮留め→位置調整→本留めの流れ",
    ],
    diagrams: [
      {
        svg: SVG_FASTENER,
        caption: "ファスナーと取付けの流れ（17.2.5）",
      },
    ],
  },

  "17.2.6": {
    plainSummary: "ガラスの取付け方法は特記による。",
  },

  "17.2.7": {
    plainSummary: "シーリング材の施工・試験は9章7節（シーリング）による。",
    why: "CWの漏水はほぼ目地から起こるため、シーリングの施工品質と接着性試験が水密性の要になります。",
  },

  "17.2.8": {
    plainSummary:
      "CW構成部材は施工中に変色・汚損・排水経路の目詰まりが発生しないよう必要に応じて養生する。",
    why: "CWには結露水や浸入水を排出する排水経路（水抜き）が組み込まれています。施工中にモルタルやゴミで目詰まりすると、水が溜まって漏水します。排水経路の保護は重要です。",
  },

  "17.3.1": {
    plainSummary:
      "この節はプレキャストコンクリートを用いたPCカーテンウォール工事に適用する。",
    glossary: [
      {
        term: "PCカーテンウォール",
        meaning:
          "工場で製造したプレキャストコンクリートパネルの外壁。重厚で耐火性が高く、タイルなどを先付けできる。",
      },
    ],
  },

  "17.3.2": {
    plainSummary:
      "コンクリートは普通/軽量（種類は特記）。品質は特記がなければFc30N/mm²・スランプ12cm・単位水量最大185kg/m³。鉄筋は特記がなければSD295。補強鉄線・シーリング材・耐火目地材・断熱材・ガラス・取付け金物・先付け仕上げ材（タイル等）は規定による。",
    why: "PCパネルは薄いコンクリート板なので、通常の躯体（Fc21〜24）より高強度（Fc30）で作ります。タイルを型枠に先付けして一体成型できるのがPCの強みで、現場でタイルを張る手間と剥落リスクを減らせます。",
  },

  "17.3.3": {
    plainSummary:
      "製品の寸法許容差は特記がなければ表17.3.1（辺長±3mm、版厚±2mm、反り5mm等）。取付け用金物の表面処理は表14.2.2（屋外はC種、屋内はE種）。構造ガスケット用アンカー溝の寸法は特記。",
  },

  "17.3.4": {
    plainSummary:
      "型枠は所要の仕上りを確保。配筋は特記がなければ計算書で承諾を受ける。鉄筋交差部を緊結（やむを得ない場合は承諾を受けて溶接）、最小かぶりは5.3.5、吊上げ・取付け金物回りは十分補強。コンクリートは密実に締め固め、脱型強度12N/mm²以上を確認後に脱型する。",
    why: "PCパネルは吊り上げて運搬・取付けするため、吊上げ金物回りの補強が重要。脱型強度12N/mm²以上を確認してから型枠を外すのは、若いコンクリートを脱型時に壊さないためです。",
    points: [
      "吊上げ・取付け金物回りは十分補強",
      "脱型強度12N/mm²以上を確認してから脱型",
    ],
  },

  "17.3.5": {
    plainSummary:
      "躯体付け金物は17.2.5(1)による。主要部材の取付け位置の許容差は特記がなければ表17.3.2（目地幅±5mm等、メタルCWより緩い）。それ以外は17.2.5(2)による。耐火構造は17.2.5(3)による。",
    why: "PC CWは重量物なので、メタルCWより取付け許容差が緩め（目地幅±5mm対±3mm）。重いパネルを高精度に据えるのは難しいためです。",
  },

  "17.3.6": {
    plainSummary: "ガラスの取付け方法は特記による。",
  },

  "17.3.7": {
    plainSummary: "耐火被覆の施工は7章9節（耐火被覆）による。",
  },

  "17.3.8": {
    plainSummary: "シーリング材の施工・試験は9章7節（シーリング）による。",
  },

  "17.3.9": {
    plainSummary: "養生は17.2.8による（変色・汚損・排水経路の目詰まり防止）。",
  },
};

export default ch17;
